// Fleet-level document requirements. Definitions live in code, not in
// `document_requirements` — the fleet doc set is a fixed catalog owned by the
// carrier feature.

import {
  requirementUnitMembers,
  requiredRequirementUnits,
  type DocumentRequirementDescriptor,
} from './document-requirement-sets'

// `pending_review` is the state a carrier upload lands in until the managing
// office approves/rejects it. Docs cloned from an already-approved onboarding
// submission bypass this and land as `approved` directly.
//
// Nothing writes `draft` any more (PET-3208, DOC-34): the upload IS the
// submission, so there is no private staging state before the office can see a
// file. The value stays in the vocabulary — as it does in
// `ONBOARDING_DOCUMENT_STATUSES` and `CARRIER_DOCUMENT_STATUSES` — so a row that
// somehow predates the backfill still renders a badge instead of nothing.
export const FLEET_DOCUMENT_STATUSES = [
  'draft',
  'pending_review',
  'approved',
  'rejected',
  'expired',
] as const
export type FleetDocumentStatus = (typeof FLEET_DOCUMENT_STATUSES)[number]

// `emblem_delivery` (PET-3167 phase 2) is a certificate the Emblem agent handed
// us through their tokenized upload link, as opposed to one the carrier owner
// uploaded themselves. Both are certificates and both need the office's review;
// the source is what lets Ops tell "the agency placed this" from "the carrier
// says this is their policy" without opening the file.
export const FLEET_DOCUMENT_SOURCES = [
  'carrier_upload',
  'onboarding_transfer',
  'emblem_delivery',
] as const
export type FleetDocumentSource = (typeof FLEET_DOCUMENT_SOURCES)[number]

export const FLEET_SUBJECT_KINDS = ['fleet_driver', 'fleet_truck'] as const
export type FleetSubjectKind = (typeof FLEET_SUBJECT_KINDS)[number]

// Gating vocabulary for the fleet catalog, mirroring how the onboarding catalog
// splits its own: the KEYS live here, the evaluation lives outside (there, in the
// backend's documentConditions; here, in `@repo/utils`, because deciding whether
// someone is 65 needs date maths and this package has no dependencies).
//
// A condition says whether a requirement APPLIES AT ALL. It says nothing about
// required vs optional — that is `isRequired`, and conflating the two is what
// PET-716 unpicked on the onboarding side.
//
// `not_self_insured` is phrased as a negative because it must fail OPEN, and the
// name should say so: this catalog governs EVERY truck, including a standard
// office's own, which carry no insurance path and still owe a registration.
// Deliberately not `needs_insurance` after the onboarding atom — that twin fails
// CLOSED on an unknown path, and one name meaning opposite things across a
// handoff is what `DOC-27` had to be written to untangle.
export const FLEET_DOCUMENT_CONDITION_KEYS = ['age_65_plus', 'not_self_insured'] as const
export type FleetDocumentConditionKey = (typeof FLEET_DOCUMENT_CONDITION_KEYS)[number]

// `oneOfGroup` members satisfy their group when any one is approved (e.g. the
// workers' comp exemption OR the certificate).
export type FleetDocumentRequirement = DocumentRequirementDescriptor & {
  // Absent means the requirement always applies.
  conditionKey?: FleetDocumentConditionKey
}

// Answers whether one atom holds for the subject being evaluated. Built by
// `fleetConditionEvaluatorFor` in `@repo/utils` from the subject's own facts —
// a driver's date of birth, a truck's insurance path.
export type FleetConditionEvaluator = (key: FleetDocumentConditionKey) => boolean

export const FLEET_DRIVER_DOCUMENT_REQUIREMENTS: readonly FleetDocumentRequirement[] = [
  {
    slug: 'cdl',
    name: 'CDL',
    description: 'Commercial Driver License (front + back).',
    isRequired: true,
    sortOrder: 10,
  },
  {
    slug: 'medical_card',
    name: 'Medical card',
    description: 'DOT medical examiner certificate.',
    isRequired: true,
    // Only from 65, matching the onboarding catalogs (`DOC-33`). Read off the
    // driver's identity date of birth, and fails OPEN on an unknown one — a
    // driver we hold no date for still owes the card rather than skipping a
    // DOT-mandated document on missing data.
    conditionKey: 'age_65_plus',
    sortOrder: 20,
  },
  {
    slug: 'workers_comp_exemption',
    name: "Workers' comp exemption",
    description: "State-issued workers' compensation exemption certificate.",
    isRequired: false,
    oneOfGroup: 'workers_comp',
    sortOrder: 30,
  },
  {
    slug: 'workers_comp_certificate',
    name: "Workers' comp certificate",
    description: "Workers' compensation insurance certificate.",
    isRequired: false,
    oneOfGroup: 'workers_comp',
    sortOrder: 40,
  },
] as const

export const FLEET_TRUCK_DOCUMENT_REQUIREMENTS: readonly FleetDocumentRequirement[] = [
  // Owed on both paths: a certificate on file is the bare minimum to dispatch a
  // truck, however the coverage was placed (`FLT-33`).
  {
    slug: 'insurance_certificate',
    name: 'Certificate of insurance',
    description: 'Commercial auto liability certificate of insurance.',
    isRequired: true,
    sortOrder: 10,
  },
  // These two exist so Emblem can underwrite the vehicle, and there is no Emblem
  // on the `has_own` path — the same gate the onboarding catalog puts on
  // `truck_registration` / `dot_inspection`. Before this condition existed the
  // two catalogs disagreed: onboarding correctly never asked a self-insured
  // carrier for either, then every promoted truck arrived 1-of-3 and was held
  // out of service on paperwork nobody had ever been asked to supply.
  {
    slug: 'registration',
    name: 'Registration',
    description: 'Vehicle registration document.',
    isRequired: true,
    conditionKey: 'not_self_insured',
    sortOrder: 20,
  },
  {
    slug: 'dot_inspection',
    name: 'DOT inspection',
    description: 'Most recent annual DOT inspection report.',
    isRequired: true,
    conditionKey: 'not_self_insured',
    sortOrder: 30,
  },
] as const

export const REQUIRED_FLEET_TRUCK_DOCUMENT_SLUGS: readonly string[] =
  FLEET_TRUCK_DOCUMENT_REQUIREMENTS.filter((r) => r.isRequired).map((r) => r.slug)

// Rollup of a truck's required-doc review state. `approved` means every
// required slug has an approved current row and the truck is dispatchable;
// `pending_docs` means at least one required slug is missing / draft /
// pending_review / rejected. Shared across API responses and the UI badges.
export const TRUCK_DOCUMENT_REVIEW_STATUSES = ['approved', 'pending_docs'] as const
export type TruckDocumentReviewStatus = (typeof TRUCK_DOCUMENT_REVIEW_STATUSES)[number]

/**
 * The WHOLE catalog for a subject kind, conditions ignored.
 *
 * Use this to look a slug up — labelling a stored document, validating an upload
 * target — where the answer must not depend on who the subject is. To decide what
 * a particular driver actually owes, use `resolveFleetDocumentRequirements`:
 * this function would hand you the medical card for a 30-year-old.
 */
export function getFleetDocumentRequirements(
  subjectKind: FleetSubjectKind,
): readonly FleetDocumentRequirement[] {
  return subjectKind === 'fleet_driver'
    ? FLEET_DRIVER_DOCUMENT_REQUIREMENTS
    : FLEET_TRUCK_DOCUMENT_REQUIREMENTS
}

export function getFleetDocumentRequirement(
  subjectKind: FleetSubjectKind,
  slug: string,
): FleetDocumentRequirement | undefined {
  return getFleetDocumentRequirements(subjectKind).find((r) => r.slug === slug)
}

/**
 * What this particular subject owes: the catalog with conditioned requirements
 * dropped when their atom does not hold.
 *
 * Every surface that asks "which documents does this driver need" — the slot
 * list, the create gate, the quote-readiness check — must go through here, so
 * they cannot disagree about whether a given person owes a medical card.
 *
 * `storedSlugs` is how a DISPLAY surface avoids hiding a file. A driver under 65
 * owes no medical card, but one may already sit in `driver_documents` — carried
 * over from onboarding, or uploaded before the gate existed. Dropping it from the
 * response makes it unreachable: the file is there, references a real blob, and
 * no UI can reach it, which is the exact failure the slug-translation comment
 * below was written about. So a conditioned-out requirement is kept when a
 * document exists for it, forced to `isRequired: false` — visible, and gating
 * nothing. `buildReviewSlots` does the same for a requirement that has left the
 * effective set.
 *
 * A GATE passes no `storedSlugs`: what the subject owes must not depend on what
 * they happen to have already sent.
 */
export function resolveFleetDocumentRequirements(
  subjectKind: FleetSubjectKind,
  applies: FleetConditionEvaluator,
  storedSlugs?: ReadonlySet<string>,
): readonly FleetDocumentRequirement[] {
  return getFleetDocumentRequirements(subjectKind).flatMap((requirement) => {
    if (!requirement.conditionKey || applies(requirement.conditionKey)) return [requirement]
    if (!storedSlugs?.has(requirement.slug)) return []
    return [{ ...requirement, isRequired: false }]
  })
}

// The onboarding catalog (`document_requirements`, per-tenant rows) and the fleet
// catalog above (fixed, in code) are two vocabularies for the same paperwork, and
// they do not agree everywhere. Translating matters because the fleet documents
// APIs build their response by iterating THIS catalog and looking each slug up:
// a row whose slug is absent from it is dropped from the response even though it
// sits in `driver_documents` / `truck_documents`, so the carrier can never see
// the file. That is not a display nit — it is a document the office asked for,
// the applicant supplied, and nobody can reach again.
const ONBOARDING_TO_FLEET_SLUG: Readonly<Record<string, string>> = {
  truck_registration: 'registration',
}

// The fleet spelling of a slug, so both catalogs can key one lookup.
export function canonicalDocumentSlug(slug: string): string {
  return ONBOARDING_TO_FLEET_SLUG[slug] ?? slug
}

// A document the wizard hangs off one half of a pair card but Fleet keeps on the
// other. Only the COI, and since PET-3167 phase 2 this is **back-compat rather
// than a live divergence**: the carrier catalog now collects the certificate at
// `truck` scope on both insurance paths, so for any application judged under the
// current catalog this mapping is the identity.
//
// It survives for the applications that predate that move, whose COI documents
// hang off an `application_driver` subject under the grandfathered driver-scope
// requirement (`DOC-03`). The `CARR-06` recovery branch can re-run the handoff for
// one of those at any time, and without this the certificate would resolve to no
// fleet home at all and be reported-and-skipped — the file present, the truck's
// required slot empty, which is the exact failure this map was written for.
const FLEET_SUBJECT_OVERRIDE: Readonly<Record<string, FleetSubjectKind>> = {
  insurance_certificate: 'fleet_truck',
}

/**
 * Where an onboarding requirement's documents belong in the fleet store: which
 * entity, and under which slug. `null` when this catalog has no home for them.
 *
 * Callers must treat `null` as an error to report rather than a row to write:
 * writing it stores a file no UI can reach. `carrierOnboardingFleetSlugs.test.ts`
 * pins that every per-unit carrier requirement resolves, so a slug added to the
 * onboarding catalog fails a test instead of vanishing after a deploy.
 */
export function fleetDocumentTargetFor(
  subjectKind: FleetSubjectKind,
  onboardingSlug: string,
): { kind: FleetSubjectKind; slug: string } | null {
  const slug = canonicalDocumentSlug(onboardingSlug)
  const kind = FLEET_SUBJECT_OVERRIDE[slug] ?? subjectKind
  return getFleetDocumentRequirement(kind, slug) ? { kind, slug } : null
}

// Multipart field name for a fleet-document file bucket. Shared so the
// backend router and the frontend mutation agree on the exact key format.
export const fleetDocFieldFor = (slug: string): string => `docs_${slug}`

// True when `filesBySlug` satisfies every required unit in `requirements` — the
// create-driver / create-truck gate. The frontend passes `File[]` per slug; the
// backend passes `File[]` via a Map. Both work here as `ArrayLike` via the
// length probe.
//
// Delegates to `requiredRequirementUnits` so this agrees with the onboarding
// completion check.
export function isFleetDocSetSatisfied(
  requirements: readonly FleetDocumentRequirement[],
  filesBySlug:
    Readonly<Record<string, { length: number } | undefined>> | Map<string, { length: number }>,
): boolean {
  const countFor = (slug: string): number =>
    (filesBySlug instanceof Map ? filesBySlug.get(slug) : filesBySlug[slug])?.length ?? 0

  return requiredRequirementUnits(requirements).every((unit) =>
    requirementUnitMembers(unit).some((req) => countFor(req.slug) > 0),
  )
}
